import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { estimateRuntime, formatRuntimeLabel } from '../../../shared/runtime';

interface Props {
  editor: Editor | null;
}

// On-camera runtime of the spoken (audio) column, sitting just above the
// AUDIO / VISUAL headings so the client sees it — and sees it recompute live as
// they edit in suggest mode — without scrolling back up to a header. Renders
// nothing when there's too little audio to estimate (estimateRuntime's
// MIN_WORDS threshold), so a near-empty script never shows a silly runtime.
export default function RuntimeBadge({ editor }: Props) {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!editor) {
      setLabel(null);
      return;
    }
    function compute() {
      const est = estimateRuntime(editor!.getJSON());
      setLabel(est ? formatRuntimeLabel(est) : null);
    }
    compute();
    editor.on('update', compute);
    return () => {
      editor.off('update', compute);
    };
  }, [editor]);

  if (!label) return null;

  return (
    <span
      className="inline-flex items-center gap-2 text-[12px] md:text-[13px] text-muted"
      title="Estimated on-camera runtime of the spoken (audio) script at ~140 words per minute"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" aria-hidden />
      {label}
    </span>
  );
}
